import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient, botSpecificResendWebhookPath } from "../../constants.ts";
import {
  Button,
  Collapsible,
  CopyableValue,
  inputClass,
  labelClass,
  PageSpinner,
} from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

export const ResendConfig = ({ id }: { id: string }) => {
  const [newApiKey, setNewApiKey] = useState("");
  const [newFromAddress, setNewFromAddress] = useState("");
  const [newWebhookSecret, setNewWebhookSecret] = useState("");
  const [editing, setEditing] = useState(false);
  const token = useAuth().user?.refresh_token;
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } }, secrets: {} },
  });
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const { bots } = data;
  const [bot] = bots;
  if (!bot) return <div>No bot found</div>;
  const isConfigured = (bot.secrets ?? []).some(
    (s: { name: string }) => s.name === "RESEND_API_KEY",
  );
  const showForm = !isConfigured || editing;
  const webhookUrl = botSpecificResendWebhookPath(bot.id);

  return (
    <Collapsible title="Resend">
      <div className="flex flex-col gap-4">
        {showForm
          ? (
            <div className="flex flex-col gap-3">
              <div>
                <label className={labelClass}>Resend API Key</label>
                <input
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.currentTarget.value)}
                  className={inputClass}
                  placeholder="re_..."
                  type="password"
                />
              </div>
              <div>
                <label className={labelClass}>From Email Address</label>
                <input
                  value={newFromAddress}
                  onChange={(e) => setNewFromAddress(e.currentTarget.value)}
                  className={inputClass}
                  placeholder="bot@yourdomain.com"
                />
              </div>
              <div>
                <label className={labelClass}>
                  Webhook Signing Secret (optional)
                </label>
                <input
                  value={newWebhookSecret}
                  onChange={(e) => setNewWebhookSecret(e.currentTarget.value)}
                  className={inputClass}
                  placeholder="whsec_..."
                  type="password"
                />
              </div>
              <CopyableValue
                label="Inbound Webhook URL (add to Resend Webhooks)"
                value={webhookUrl}
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (!token) return;
                    toast.promise(
                      apiClient({
                        token,
                        endpoint: "set-resend",
                        payload: {
                          botId: bot.id,
                          apiKey: newApiKey,
                          fromAddress: newFromAddress,
                          ...(newWebhookSecret
                            ? { webhookSecret: newWebhookSecret }
                            : {}),
                        },
                      }).then((res) => {
                        if (!res.success) throw new Error(res.error);
                        setNewApiKey("");
                        setNewFromAddress("");
                        setNewWebhookSecret("");
                        setEditing(false);
                      }),
                      {
                        loading: "Connecting Resend...",
                        success: "Resend connected!",
                        error: (err) =>
                          `Failed to connect Resend: ${err.message}`,
                      },
                    );
                  }}
                  disabled={!newApiKey || !newFromAddress}
                  className="w-full md:w-auto"
                >
                  Connect Resend
                </Button>
                {editing && (
                  <Button
                    onClick={() => {
                      setEditing(false);
                      setNewApiKey("");
                      setNewFromAddress("");
                      setNewWebhookSecret("");
                    }}
                    className="w-full md:w-auto"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )
          : (
            <div className="flex flex-col gap-3">
              <CopyableValue
                label="Inbound Webhook URL"
                value={webhookUrl}
              />
              <div className="text-sm text-text-secondary-light dark:text-text-secondary">
                Resend is configured.
              </div>
              <Button
                onClick={() => setEditing(true)}
                className="w-full md:w-auto"
              >
                Reconfigure
              </Button>
            </div>
          )}
      </div>
    </Collapsible>
  );
};
